import React, { FC, useCallback, useContext, useEffect, useState } from 'react';

import { Table, Typography, Card as CardAnt } from 'antd';

import {
  ColSize,
  ContentSection,
  Form,
  InputForm,
  RowSize,
  SelectForm,
} from '../../../../../../../../../../../../../shared/components';

import { GenericDataFormContext, QuotationInfoContext } from '../../../../../../../../../shared/contexts';
import { StepFourFormContext } from '../../../../../../contexts/StepFourForm';
import { StepTwoFormContext } from '../../../../../../../StepTwo/contexts/StepTwoForm/StepTwoFormContext';

import { CivilQuotationComponent } from '../../../../../../../../../../../../../shared/interfaces/services/civil-quotation-service';

import {
  formattedPrice,
  ValidateForm,
} from '../../../../../../../../../../../../../shared/utils';
import './index.css';

import { COLORS } from '../../../../../../../../../../../../../config/themes/colors';
import { TransferRightsForm } from '../../../TransferRightsForm';

interface Coverage {
  codigo: string;
  description: string;
  familyCode?: string;
  familyDescription?: string;
  key: string;
  riskCode: string;
  sumInsured: string;
  typeCoverage?: string;
}

interface CoverageGroup {
  [familyCode: string]: Coverage[];
}

interface CoveragesProps {
  civilComponent: CivilQuotationComponent;
}

const MOCK_TABLE_COLUMNS_HEADER = [
  {
    title: 'Descripción de cobertura',
    dataIndex: 'description',
    width: '50%',
  },
  {
    title: 'Suma asegurada',
    dataIndex: 'sumInsured',
    width: '50%',
  },
];

export const FormCoverages: FC<CoveragesProps> = ({ civilComponent }) => {
  const validateForm = new ValidateForm();

  const { propertyDataForm } = useContext(StepFourFormContext);
  const { stepTwoForm } = useContext(StepTwoFormContext);
  const { provinceList, allLocalitiesPerProvince, allCoveragesPerPlan } =
    useContext(GenericDataFormContext);

  const [coveragesListInfo, setCoveragesListInfo] = useState<CoverageGroup[]>(
    []
  );

  const provinceValueByTab = stepTwoForm?.getFieldValue(
    `province-tab-${civilComponent.nroComponente}`
  );
  const locationValueByTab = stepTwoForm?.getFieldValue(
    `location-tab-${civilComponent.nroComponente}`
  );
  const localitiesPerProvince = allLocalitiesPerProvince.find(
    (loc) => String(loc.provinceCode) === String(provinceValueByTab)
  )?.localitiesOptions;

  const planSelectedCode = stepTwoForm?.getFieldValue(
    `plan-tab-${civilComponent.nroComponente}`
  );

  const setValuesForm = () => {
    propertyDataForm.setFieldValue(
      `propertyDataProvince${civilComponent.nroComponente}`,
      provinceValueByTab
    );
    propertyDataForm.setFieldValue(
      `propertyDataLocality${civilComponent.nroComponente}`,
      locationValueByTab
    );
  };

  const propertyDataAddressValueByTab = stepTwoForm?.getFieldValue(`propertyDataAddress${civilComponent.nroComponente}`);

  useEffect(() => {
    setValuesForm();
  }, [civilComponent]);

  const setCompleteCoveragesListInfo = useCallback(() => {
    const matchingCoveragesByTab = allCoveragesPerPlan.find(
      (el) => String(el.plan as string) === String(planSelectedCode)
    );

    if (matchingCoveragesByTab) {
      const filteredCoverages = matchingCoveragesByTab.coverages.filter(
        (planCoverage) =>
          civilComponent.cobertura.some(
            (cov) => cov.cobertura === planCoverage.codigo
          )
      );

      const coveragesListInfo = filteredCoverages.map((planCoverage) => {
        const matchedCoverage = civilComponent.cobertura.find(
          (cov) => cov.cobertura === planCoverage.codigo
        );

        let familyDescription;

        if (planCoverage?.codigoFamilia) {
          familyDescription = planCoverage.descripcionFamilia;
        } else if (planCoverage.tipoDeCobertura === 'O') {
          familyDescription = 'ADICIONAL';
        } else {
          familyDescription = 'OTRAS';
        }

        return {
          codigo: planCoverage.codigo,
          description: planCoverage.descripcionLarga,
          familyCode:
            planCoverage?.codigoFamilia ??
            (planCoverage.tipoDeCobertura === 'O'
              ? 'additional'
              : 'noFamilyCode'),
          familyDescription,
          key: planCoverage.codigo,
          riskCode: planCoverage.codigoRiesgo,
          sumInsured: `$ ${formattedPrice(
            `${matchedCoverage ? matchedCoverage.sumaAsegurada : 0}`
          )}`,
          typeCoverage: planCoverage?.tipoDeCobertura,
        };
      });

      const groupedData = coveragesListInfo.reduce<{
        [key: string]: Coverage[];
      }>((acc, item) => {
        let codeFamilyData: string;

        codeFamilyData = item.familyCode;

        if (!acc[codeFamilyData]) acc[codeFamilyData] = [];

        acc[codeFamilyData].push(item);
        return acc;
      }, {});

      const desiredOrder = ['noFamilyCode', 'additional'];
      const orderedGroupedData: { [key: string]: Coverage[] } = {};

      desiredOrder.forEach((key) => {
        if (groupedData[key]) orderedGroupedData[key] = groupedData[key];
      });

      Object.keys(groupedData)
        .filter((key) => !desiredOrder.includes(key))
        .forEach((key) => {
          orderedGroupedData[key] = groupedData[key];
        });

      setCoveragesListInfo([orderedGroupedData]);

      return;
    }

    setCoveragesListInfo([]);
  }, [
    allCoveragesPerPlan,
    civilComponent.cobertura,
    civilComponent.nroComponente,
    stepTwoForm,
  ]);

  useEffect(() => {
    setCompleteCoveragesListInfo();
  }, [setCompleteCoveragesListInfo]);

  const { renewalQuotationState } = useContext(QuotationInfoContext);
  const isRenewal = !!renewalQuotationState;   

  return (
    <CardAnt>
      <ContentSection level='h3' title='Datos del bien'>
        <Form
          formProps={{
            form: propertyDataForm,
            name: `activity-form-${civilComponent.nroComponente}`,
          }}
        >
          <RowSize>
            <ColSize size={25}>
              <InputForm
                formProps={{
                  label: 'Domicilio:',
                  name: `propertyDataAddress${civilComponent.nroComponente}`,
                  rules: validateForm.generateRules({
                    label: 'domicilio',
                    rules: ['required'],
                  }),
                  initialValue: isRenewal ? (propertyDataAddressValueByTab ?? '') : '',
                }}
                inputProps={{ placeholder: 'Senillosa 342 1B', disabled: !!propertyDataAddressValueByTab && isRenewal, }}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Provincia:',
                  name: `propertyDataProvince${civilComponent.nroComponente}`,
                }}
                selectProps={{
                  disabled: true,
                  loading: provinceList?.length === 0,
                  options: provinceList,
                  placeholder: 'Seleccionar',
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Localidad:',
                  name: `propertyDataLocality${civilComponent.nroComponente}`,
                }}
                selectProps={{
                  disabled: true,
                  options: localitiesPerProvince,
                  placeholder: 'Seleccionar',
                }}
              />
            </ColSize>
          </RowSize>
        </Form>
      </ContentSection>

      <ContentSection level='h3' title='Cobertura' hasCard={false}>
        {coveragesListInfo?.map((coverageGroup: any) => {
          const familyCodes = Object.keys(coverageGroup);

          return familyCodes.map((familyCode, index) => {
            const coverages = coverageGroup[familyCode];

            return (
              <React.Fragment key={`${familyCode}-${index}`}>
                <ColSize size={100}>
                  <Typography.Title
                    level={5}
                    style={{
                      margin: 0,
                      marginBottom: 10,
                      color: COLORS.secondary[100],
                    }}
                  >
                    {coverages[0]?.familyDescription}
                  </Typography.Title>
                </ColSize>
                <Table
                  bordered
                  className='person-table'
                  columns={MOCK_TABLE_COLUMNS_HEADER}
                  dataSource={coverages}
                  pagination={false}
                  scroll={{ x: 'auto' }}
                />
              </React.Fragment>
            );
          });
        })}
      </ContentSection>
      <TransferRightsForm civilComponent={civilComponent} />
    </CardAnt>
  );
};
